import { bindActionCreators, createNextState } from '@reduxjs/toolkit';
import { RadioButtonRow, space } from 'folio-common-components';
import * as React from 'react';
import { connect } from 'react-redux';
import { Labels } from '../../messages';
import type { Dispatch, RootState } from '../../state';
import {
  type Person,
  type Role,
  newPerson,
  selectors,
  thunks,
} from '../../state/editor';
import { isValidPnum } from '../../utils/validators';
import { EditItem } from '../OwnerList/EditItem';
import { PersonInput } from '../PersonInput';
import { Box, BoxHeading } from '../box';
import { Button, TextButton } from '../buttons';

type PersonWithRolesAndOwnership = ReturnType<
  typeof selectors.peopleWithRolesAndOwnership
>[number];

interface Props {
  personId?: string;
  person: PersonWithRolesAndOwnership;
  boardMembers: Person[];
  chair: Person;
  onDone: () => any;
  onSavePerson: (person: PersonWithRolesAndOwnership) => any;
  editType: 'edit' | 'new';
}

interface State {
  person: PersonWithRolesAndOwnership;
  hasClickedSave: boolean;
}

class BaseBoardMemberForm extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const person =
      props.person ||
      Object.assign(newPerson(), {
        shares: 0,
        roles: ['boardMember'],
      });

    this.state = {
      person,
      hasClickedSave: false,
    };
  }

  updatePerson = (person: { name?: string; pNum?: string; email?: string }) => {
    this.setState(
      createNextState((draft: State) => {
        Object.assign(draft.person, person);
      }),
    );
  };

  toggleRole = (role: Role) => {
    this.setState(
      createNextState((draft: State) => {
        draft.person.roles = [role];
      }),
    );
  };

  save = () => {
    this.props.onSavePerson(this.state.person);
    this.props.onDone();
  };

  render() {
    const { onDone, editType, boardMembers, chair } = this.props;
    const { person, hasClickedSave } = this.state;
    const boardMemberIds = boardMembers.map(person => person.id);
    const isNew = editType === 'new';
    return (
      <EditItem>
        <BoxHeading>
          {isNew ? 'Legg til nytt styremedlem' : 'Endre detaljer'}
        </BoxHeading>
        <Box>
          <form
            noValidate={true}
            onSubmit={evt => {
              evt.preventDefault();
            }}
            data-testid="RolePersonForm"
          >
            <PersonInput
              inputs={['autoName', 'pNum', 'email', 'phone']}
              disableAutocomplete={true}
              person={person}
              onChange={p => {
                this.setState(() => ({ person: { ...person, ...p } }));
              }}
              validate={hasClickedSave}
              autoSuggestBlacklist={boardMemberIds}
              editType={editType}
            />
          </form>
          <div css={space([48], 'margin-vertical')}>
            <RadioButtonRow
              inputProps={{
                checked: person.roles.includes('boardMember'),
                onChange: () => this.toggleRole('boardMember'),
                name: 'role',
              }}
              id="boardMember"
              label="Styremedlem"
              description="Med på å lede selskapet, og har stemmerett."
            />

            <RadioButtonRow
              inputProps={{
                checked: person.roles.includes('deputy'),
                onChange: () => this.toggleRole('deputy'),
                name: 'role',
              }}
              id="deputy"
              label="Vara"
              description="Deltar på styremøter med stemmerett, når noen av medlemmene er forhindret."
            />
          </div>
          <Button
            onClick={() => {
              this.setState({ hasClickedSave: true });
              if (!isValidPnum(person.pNum)) {
                return;
              }

              // Chair can not be overwritten via the board member form
              if (chair.pNum === person.pNum) {
                return;
              }

              const boardMemberPnums = boardMembers.map(person => person.pNum);
              if (
                editType === 'new' &&
                boardMemberPnums.includes(person.pNum)
              ) {
                return;
              }

              this.save();
            }}
            css={space([24], 'margin-right')}
          >
            <Labels.Done />
          </Button>{' '}
          <TextButton onClick={onDone}>
            <Labels.Cancel />
          </TextButton>
        </Box>
      </EditItem>
    );
  }
}

function mapStateToProps(state: RootState, props: { personId?: string }) {
  const users = selectors.peopleWithRolesAndOwnership(state);
  const user = users.find(e => e.id === props.personId);
  const boardMembers = selectors.getAllBoardMembers(state);

  return {
    person: user,
    boardMembers,
    chair: selectors.getChair(state),
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return bindActionCreators(
    {
      onSavePerson: thunks.updateRolePerson,
    },
    dispatch,
  );
}

export const BoardMemberForm = connect(
  mapStateToProps,
  mapDispatchToProps,
)(BaseBoardMemberForm);
